import React, { useEffect, useState } from 'react'
import Text from '@embracesbs/component-text'
import { MaintenanceType, NewMaintenance, DateRange } from '../../../Interfaces/Types'
import DatePicker from '@embracesbs/component-datepicker'
import Container from '@embracesbs/component-container/Container'
import TextField from '@embracesbs/component-textfield/TextField'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper/TextFieldWrapper'
import Button2 from '@embracesbs/component-button2/Button2'
import { BoldText, ContentText, InputField, TextContainer, TextContainerHeading } from '../../StyledComponents/Components'
import { useTenants } from '../../Context/TenantContext'
import { useApi, useAuth } from '../../../Hooks'

const style = {
  maxHeight: '80%', // Ensures that the div doesn't grow beyond the outer div's height
  overflow: 'auto', // Enables scrolling
  paddingRight: '15px'
}

interface TenantDetailsProps { }

const Maintenance: React.FC<TenantDetailsProps> = ({ }) => {
  const { fetchApi } = useApi()
  const { isLoggedIn } = useAuth()
  const { selectedTenantId } = useTenants()
  const [maintenance, setMaintenance] = useState<MaintenanceType>({ hasMaintenance: false })

  const [value, setValue] = useState<DateRange | undefined>({
    from: new Date(),
    to: new Date()
  })
  const [newMaintenance, setNewMaintenance] = useState<NewMaintenance>({
    startTime: '12:00',
    endTime: '12:00',
    title: JSON.stringify({ en: '', nl: '' }, null, 2),
    description: JSON.stringify({ en: '', nl: '' }, null, 2)
  })

  const deleteMaintenance = () => {
    fetchApi({
      endpoint: `/api/tenant/${selectedTenantId}/tenantsconfigurationruntime/maintenance`,
      method: 'DELETE'
    })
      .then(() => {
        console.log('Maintenance deleted successfully, updating UI...')
        setMaintenance({ hasMaintenance: false })
        fetchMaintenance()
      })
      .catch((error) => {
        alert(error)
        console.error('Error removing maintenance:', error)
      })
  }

  useEffect(() => {
    if (selectedTenantId !== null && isLoggedIn) fetchMaintenance()
  }, [selectedTenantId, isLoggedIn])

  const fetchMaintenance = async () => {
    fetchApi({
      endpoint: `/api/tenant/${selectedTenantId}/tenantsconfigurationruntime/maintenance`,
      method: 'GET'
    })
      .then((response) => {
        if (response && response.hasMaintenance) {
          setMaintenance(response)
        } else {
          setMaintenance({ hasMaintenance: false })
        }
      })
      .catch((error) => {
        console.log('No maintenance found or error occurred:', error)
        setMaintenance({ hasMaintenance: false })
      })
  }

  function formatDateString(date: Date | undefined) {
    if (date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}T`
    }
    return ''
  }

  const handleSubmit = () => {
    const localStartDateTime = formatDateString(value?.from) + newMaintenance.startTime + ':00'
    const localEndDateTime = formatDateString(value?.to) + newMaintenance.endTime + ':00'

    const startTimeUTC = new Date(localStartDateTime).toISOString()
    const endTimeUTC = new Date(localEndDateTime).toISOString()

    const maintenancePayload = {
      ...newMaintenance,
      startTime: startTimeUTC,
      endTime: endTimeUTC,
      title: JSON.parse(newMaintenance.title),
      description: JSON.parse(newMaintenance.description)
    }

    fetchApi({
      endpoint: `/api/tenant/${selectedTenantId}/tenantsconfigurationruntime/maintenance`,
      method: 'POST',
      body: maintenancePayload
    })
      .then(() => {
        console.log('Maintenance created successfully, updating UI...')
        setMaintenance({ ...maintenancePayload, hasMaintenance: true })
        fetchMaintenance()
      })
      .catch((error) => {
        alert(error)
        console.error('Error adding maintenance:', error)
      })
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setNewMaintenance((prev) => ({ ...prev, [name]: value }))
  }

  if (!selectedTenantId) {
    return (
      <Text align='center' textColor='grey'>
        Please select a tenant
      </Text>
    )
  }

  return (
    <div style={style}>
      {maintenance?.hasMaintenance ? (
        <TextContainer>
          <TextContainerHeading>Maintenance</TextContainerHeading>
          <BoldText>
            Title en: <ContentText>{maintenance?.title?.en}</ContentText>
          </BoldText>
          <BoldText>
            Title en: <ContentText>{maintenance?.title?.en}</ContentText>
          </BoldText>
          <BoldText>
            Description en: <ContentText>{maintenance?.description?.en}</ContentText>
          </BoldText>
          <BoldText>
            Title nl: <ContentText>{maintenance?.title?.nl}</ContentText>
          </BoldText>
          <BoldText>
            Description nl: <ContentText>{maintenance?.description?.nl}</ContentText>
          </BoldText>
          <BoldText>
            Start (Local): <ContentText>{maintenance.startTime ? new Date(maintenance.startTime).toLocaleString() : ''}</ContentText>
          </BoldText>
          <BoldText>
            Start (UTC): <ContentText>{maintenance.startTime ? new Date(maintenance.startTime).toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC' : ''}</ContentText>
          </BoldText>
          <BoldText>
            End (Local): <ContentText>{maintenance.endTime ? new Date(maintenance.endTime).toLocaleString() : ''}</ContentText>
          </BoldText>
          <BoldText>
            End (UTC): <ContentText>{maintenance.endTime ? new Date(maintenance.endTime).toLocaleString('en-GB', { timeZone: 'UTC' }) + ' UTC' : ''}</ContentText>
          </BoldText>
          <BoldText>
            bypassUrl:{' '}
            <ContentText>
              {' '}
              <a href={`https://${maintenance.url}?maintanancekey=${maintenance.key}`} target='_blank' rel='noreferrer'>
                {maintenance.url}
              </a>
            </ContentText>
          </BoldText>
          <Button2
            buttonRadius='8px'
            buttonSize='40px'
            text={'Delete Maintenance'}
            isDanger={true}
            onClick={deleteMaintenance}
            variant='contained'
          />
        </TextContainer>
      ) : (
        <div>
          <Container>
            <TextFieldWrapper labelText={'Date range'}>
              <InputField readOnly placeholder='Select date range' value={`${value?.from.toDateString()}-${value?.to.toDateString()}`} />
            </TextFieldWrapper>
            <DatePicker
              isRange
              selectedDayFrom={value?.from}
              selectedDayTo={value?.to}
              onSelectingDays={({ selectedDayFrom, selectedDayTo }: { selectedDayFrom: Date; selectedDayTo: Date }) => {
                const startOfFromDay = new Date(selectedDayFrom)
                startOfFromDay.setHours(0, 0, 0)
                const endOfToDay = new Date(selectedDayTo)
                endOfToDay.setHours(23, 59, 59)
                setValue({
                  from: startOfFromDay,
                  to: endOfToDay
                })
              }}
              isRightButtonDisabled={!value}
            />
          </Container>
          <Container>
            <TextFieldWrapper labelText={'Start Time (Local - HH:MM)'}>
              <TextField name='startTime' value={newMaintenance.startTime} onChange={handleChange} type='text' variant='default' placeholder='12:00' />
            </TextFieldWrapper>
            <TextFieldWrapper labelText={'End Time (Local - HH:MM)'}>
              <TextField name='endTime' value={newMaintenance.endTime} onChange={handleChange} type='text' variant='default' placeholder='13:00' />
            </TextFieldWrapper>
          </Container>
          <Container>
            <TextFieldWrapper labelText={'Title'}>
              <TextField name='title' value={newMaintenance.title} onChange={handleChange} type='text' variant='default' />
            </TextFieldWrapper>
          </Container>
          <Container>
            <TextFieldWrapper labelText={'Description'}>
              <TextField name='description' value={newMaintenance.description} onChange={handleChange} type='text' variant='default' />
            </TextFieldWrapper>
          </Container>
          <Container>
            <Button2 buttonRadius='8px' buttonSize='40px' text={'Add Maintenance'} onClick={handleSubmit} variant='contained' />
          </Container>
        </div>
      )}
    </div>
  )
}

export default Maintenance
